import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from "@tanstack/react-table";

import { ListSkeleton, Skeleton } from "@nexus/ui";
import { cn } from "@/lib/utils";

/**
 * Shared shell for every "browse a list of records" page (dashboards,
 * tenants, reports, …): a sortable desktop table, falling back to a stacked
 * card list on mobile. Callers own the columns (desktop) and the card body
 * (mobile) — everything else (borders, spacing, sentence-case headers,
 * hover/focus behavior, keyboard activation) is identical across lists so
 * they read as one system rather than one-off pages. The row count belongs to
 * the list's `SearchFilterBar`; the table does not repeat it in a footer.
 */
interface ListTableProps<T> {
  data: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  getRowId: (row: T) => string;
  /** Omit for a purely informational table (e.g. Tenants) with no row destination. */
  onRowClick?: (row: T) => void;
  /** Cells/cards contain a real title link: retain native row semantics and avoid a second tab stop. */
  rowHasLink?: boolean;
  rowAriaLabel?: (row: T) => string;
  isRowSelected?: (row: T) => boolean;
  /** Card content shown for one row on mobile, in place of the desktop columns. */
  renderMobileCard: (row: T) => ReactNode;
}

function activateFromKeyboard<T>(
  event: KeyboardEvent<HTMLElement>,
  row: T,
  onRowClick: (row: T) => void,
) {
  if (event.target !== event.currentTarget) return;
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  onRowClick(row);
}

export function ListTable<T>({
  data,
  columns,
  getRowId,
  onRowClick,
  rowAriaLabel,
  isRowSelected,
  rowHasLink = false,
  renderMobileCard,
}: ListTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
  });

  const keyboardRow = Boolean(onRowClick) && !rowHasLink;
  const activate = (event: MouseEvent<HTMLElement>, row: T) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    if (
      event.target instanceof Element &&
      event.target.closest("a, button, input, select, textarea, [role='button'], [role='menuitem']")
    )
      return;
    if (window.getSelection()?.toString()) return;
    onRowClick?.(row);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-surface-elevated">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-muted"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-border">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                role={keyboardRow ? "link" : undefined}
                tabIndex={keyboardRow ? 0 : undefined}
                aria-label={keyboardRow ? rowAriaLabel?.(row.original) : undefined}
                onClick={onRowClick ? (event) => activate(event, row.original) : undefined}
                onKeyDown={
                  onRowClick && keyboardRow
                    ? (event) => activateFromKeyboard(event, row.original, onRowClick)
                    : undefined
                }
                className={cn(
                  onRowClick
                    ? "group/row cursor-pointer transition-colors hover:bg-surface-elevated focus-within:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-cyan"
                    : "group/row transition-colors hover:bg-surface-elevated",
                  isRowSelected?.(row.original) &&
                    "bg-row-selected hover:bg-row-selected-hover focus-within:bg-row-selected-hover",
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 align-middle last:text-right">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="divide-y divide-border md:hidden">
        {data.map((row) => (
          <div
            key={getRowId(row)}
            role={keyboardRow ? "link" : undefined}
            tabIndex={keyboardRow ? 0 : undefined}
            aria-label={keyboardRow ? rowAriaLabel?.(row) : undefined}
            onClick={onRowClick ? (event) => activate(event, row) : undefined}
            onKeyDown={
              onRowClick && keyboardRow
                ? (event) => activateFromKeyboard(event, row, onRowClick)
                : undefined
            }
            className={cn(
              onRowClick
                ? "group/row cursor-pointer px-4 py-3 transition-colors hover:bg-surface-elevated focus-within:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-cyan"
                : "px-4 py-3",
              isRowSelected?.(row) &&
                "bg-row-selected hover:bg-row-selected-hover focus-within:bg-row-selected-hover",
            )}
          >
            {renderMobileCard(row)}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Matching loading placeholder — same shell, `columnCount` blank cells per row. */
export function ListTableSkeleton({
  label,
  columnCount = 4,
  rows = 5,
}: {
  label: string;
  columnCount?: number;
  rows?: number;
}) {
  const gridStyle = { gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` };

  return (
    <div>
      <div
        className="hidden overflow-hidden rounded-lg border border-border bg-surface md:block"
        role="status"
        aria-label={label}
        aria-busy="true"
      >
        <div
          className="grid gap-4 border-b border-border bg-surface-elevated px-4 py-3"
          style={gridStyle}
        >
          {Array.from({ length: columnCount }, (_, index) => (
            <Skeleton key={index} className="h-3 w-2/3" />
          ))}
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: rows }, (_, index) => (
            <div key={index} className="grid gap-4 px-4 py-4" style={gridStyle} aria-hidden>
              {Array.from({ length: columnCount }, (_, cell) => (
                <Skeleton key={cell} className={cell === 0 ? "h-4 w-3/4" : "h-4 w-1/2"} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="md:hidden">
        <ListSkeleton label={label} />
      </div>
    </div>
  );
}
